import type {
    InfinityCommunityMember,
    InfinityLevelData,
} from "../../../../Redux/Teams/types";

export type SamriddhiStatus = "completed" | "pending";

export type SamriddhiMilestone = {
    id: string;
    level: number;
    milestone: string;
    contributionRequirement: number;
    activeContributors: number;
    status: SamriddhiStatus;
    searchText: string;
};

export type SamriddhiNetworkMember = {
    id: string;
    milestone: string;
    partnerNetworkId: string;
    partnerNetworkName: string;
    referringPartnerId: string;
    referringPartnerName: string;
    searchText: string;
};

export const mapInfinityLevelToMilestone = (
    level: number,
    levelData: InfinityLevelData
): SamriddhiMilestone => {
    const milestone = `Milestone - ${level}`;
    const status: SamriddhiStatus =
        levelData.remaining === 0 ? "completed" : "pending";

    return {
        id: `milestone-${level}`,
        level,
        milestone,
        contributionRequirement: levelData.capacity,
        activeContributors: levelData.totalMembers,
        status,
        searchText: [
            milestone,
            level,
            levelData.capacity,
            levelData.totalMembers,
            levelData.remaining,
            status,
        ]
            .join(" ")
            .toLowerCase(),
    };
};

export const mapInfinityLevelsToMilestones = (
    levels: Record<number, InfinityLevelData>
): SamriddhiMilestone[] =>
    Object.entries(levels)
        .map(([level, levelData]) =>
            mapInfinityLevelToMilestone(Number(level), levelData)
        )
        .filter((milestone) => Number.isInteger(milestone.level) && milestone.level > 0)
        .sort((a, b) => a.level - b.level);

export const mapInfinityMemberToNetworkMember = (
    member: InfinityCommunityMember,
    level: number,
    index: number
): SamriddhiNetworkMember => {
    const partnerNetworkId =
        member.userId?.registerId || member.registerId || "N/A";
    const partnerNetworkName =
        member.userId?.fullName || member.fullName || "N/A";
    const referringPartnerId =
        member.uplinerId || member.userId?.myReferralId || "N/A";
    const referringPartnerName =
        member.parentName || member.userId?.myReferralName || "N/A";
    const milestone = `Milestone - ${level}`;

    return {
        id: member._id || member.userId?._id || `network-member-${index}`,
        milestone,
        partnerNetworkId,
        partnerNetworkName,
        referringPartnerId,
        referringPartnerName,
        searchText: [
            milestone,
            partnerNetworkId,
            partnerNetworkName,
            referringPartnerId,
            referringPartnerName,
            member.userId?.emailId,
            member.registerId,
            member.fullName,
            member.parentId,
        ]
            .filter((value): value is string => Boolean(value))
            .join(" ")
            .toLowerCase(),
    };
};

export const parseSamriddhiLevel = (value?: string) => {
    const match = value?.match(/^(?:milestone-)?(\d+)$/i);
    if (!match) {
        return null;
    }

    const level = Number(match[1]);
    return Number.isInteger(level) && level > 0 ? level : null;
};
